import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { PillButton } from "./PillButton";
import { cn } from "@/lib/utils";

type Variant = "hero" | "purple" | "teal" | "sunset";

const variantClass: Record<Variant, string> = {
  hero: "mkt-gradient-hero",
  purple: "mkt-gradient-purple",
  teal: "mkt-gradient-teal",
  sunset: "mkt-gradient-sunset",
};

export const FeatureSection = ({
  eyebrow,
  title,
  description,
  cta = "Start For Free",
  ctaHref = "/auth?mode=signup",
  variant = "hero",
  mockup,
  reverse = false,
  proofChip,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  cta?: string;
  ctaHref?: string;
  variant?: Variant;
  mockup: ReactNode;
  reverse?: boolean;
  proofChip?: string;
}) => {
  const navigate = useNavigate();
  return (
    <section className="container mx-auto px-3 lg:px-8 py-12 lg:py-20">
      <div className="max-w-3xl mx-auto text-center mb-10 lg:mb-14">
        {eyebrow && (
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--mkt-primary))] mb-3">
            {eyebrow}
          </div>
        )}
        <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground">
          {title}
        </h2>
        <p className="mt-5 text-lg text-muted-foreground max-w-xl mx-auto">{description}</p>
        {proofChip && (
          <div className="mt-5 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[hsl(var(--mkt-primary)/0.10)] text-[hsl(var(--mkt-primary-deep))] text-xs font-semibold">
            {proofChip}
          </div>
        )}
        <div className="mt-7 flex justify-center">
          <PillButton variant="primary" size="lg" onClick={() => navigate(ctaHref)}>
            {cta}
          </PillButton>
        </div>
      </div>

      <div
        className={cn(
          "relative rounded-[2rem] overflow-hidden mkt-grain p-6 sm:p-12 lg:p-16",
          variantClass[variant]
        )}
      >
        <div className="absolute -right-20 top-10 w-[400px] h-[400px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="relative">{mockup}</div>
      </div>
    </section>
  );
};
